import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import site from '../../data/site';
import experiences from '../../data/experiences';
import projects, { PROJECT_GROUPS } from '../../data/projects';
import useScrollProgress from '../Utils/useScrollProgress';
import scrollToSection from '../Utils/scrollToSection';
import styles from './Nav.module.css';

const ICONS = { github: faGithub, linkedin: faLinkedin, email: faEnvelope };

export default function Nav() {
  const [open, setOpen] = useState(false);
  const progress = useScrollProgress();
  const { pathname } = useLocation();
  const isHome = pathname === '/' || pathname === '';

  // Close the mobile panel on navigation.
  useEffect(() => { setOpen(false); }, [pathname]);

  // Lock body scroll while the panel is open.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const menu = [
    {
      label: 'Experience',
      items: experiences.map((e) => ({
        key: e.id, to: e.route, external: e.external,
        primary: e.org, secondary: e.role,
      })),
    },
    ...PROJECT_GROUPS.map((g) => ({
      label: g.label,
      items: projects
        .filter((p) => p.group === g.id)
        .map((p) => ({ key: p.id, to: p.route, primary: p.title, secondary: p.event || String(p.year) })),
    })),
  ];

  return (
    <>
      <header className={styles.nav}>
        <div className={`${styles.inner} mc-shell`}>
          <Link to="/" className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true" />
            <span className={styles.brandText}>{site.shortName}</span>
          </Link>

          <nav className={styles.links} aria-label="Primary">
            {/* The timeline sits at the very top of the home page, so this is
                just "back to the top". From a detail page plain navigation
                already lands there, since ScrollToTop resets on route change. */}
            <Link
              to="/"
              className={styles.link}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                if (isHome) {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              Experience
            </Link>

            {/* Real link so middle/cmd-click still opens the home page. On the
                home page we intercept and scroll; from a detail page we let
                react-router navigate and hand Home the target via state. */}
            <Link
              to="/"
              state={{ scrollTo: 'projects' }}
              className={styles.link}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                if (isHome) {
                  e.preventDefault();
                  scrollToSection('projects');
                }
              }}
            >
              Projects
            </Link>
          </nav>

          <div className={styles.right}>
            <ul className={styles.socials}>
              {site.socials.map((s) => (
                <li key={s.id}>
                  <a
                    href={s.href}
                    aria-label={s.label}
                    className={styles.social}
                    target={s.id === 'email' ? undefined : '_blank'}
                    rel={s.id === 'email' ? undefined : 'noreferrer'}
                  >
                    <FontAwesomeIcon icon={ICONS[s.id]} />
                  </a>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className={styles.toggle}
              aria-expanded={open}
              aria-controls="nav-panel"
              onClick={() => setOpen((v) => !v)}
            >
              <span className={styles.toggleBars} data-open={open} aria-hidden="true" />
              <span className={styles.srOnly}>{open ? 'Close menu' : 'Open menu'}</span>
            </button>
          </div>
        </div>

        <span
          className={styles.progress}
          aria-hidden="true"
          style={{ transform: `scaleX(${progress})` }}
        />
      </header>

      <div id="nav-panel" className={styles.panel} data-open={open}>
        <div className={`${styles.panelInner} mc-shell`}>
          {menu.map((section) => (
            <section key={section.label} className={styles.panelSection}>
              <h2 className="mc-eyebrow">{section.label}</h2>
              <ul className={styles.panelList}>
                {section.items.map((it) => (
                  <li key={it.key}>
                    {it.external ? (
                      <a className={styles.panelLink} href={it.to} target="_blank" rel="noreferrer">
                        <span className={styles.panelPrimary}>{it.primary}</span>
                        <span className={styles.panelSecondary}>{it.secondary}</span>
                      </a>
                    ) : (
                      <Link className={styles.panelLink} to={it.to}>
                        <span className={styles.panelPrimary}>{it.primary}</span>
                        <span className={styles.panelSecondary}>{it.secondary}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}
